import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import { type IPOSProduct } from "@/services";
import { usePOSProducts } from "@/hooks/usePOSData";
import { useCategories } from "@/hooks/useStoreData";

import {
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  QrCode,
  CheckCircle,
  XCircle,
  Store,
  Package,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  Search,
  User,
  DollarSign,
  CreditCard,
  Wallet,
  Gift,
} from "lucide-react";
import { userService } from "@/services";
import orderService, { OrderData } from "@/services/orders/orderService";
import POSService, { ISaleData, IPaymentData, IPOS } from "@/services/pos/posService";
import { useToast } from "@/hooks/useToast";
import { QrScanner } from "@/components/business/qr/QrScanner";
// import { OrderQueue } from "@/components/business/orders/OrderQueue";
import { qrService, type QrValidationResult } from "@/services";
import QRDeliveryModal from "@/components/business/qr/QRDeliveryModal";
import CashSessionStatus from "@/components/business/pos/CashSessionStatus";
import { cashSessionService } from "@/services";

import POSSelection from "@/components/business/pos/POSSelection";

/**
 * Interfaz para los items del ticket
 */
interface ITicketItem {
  productId: string;
  name: string;
  price: number;
  quantity: number;
  subtotal: number;
}

/**
 * Formatea el precio como moneda chilena
 */
const formatPrice = (price: number): string => {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

/**
 * Determina si el POS actual está en modo de pedidos por lotes
 */
const isBatchMode = (posId?: number): boolean => {
  return posId === 2; // POS 2 (Pérgola) usa modo por lotes
};

/**
 * Página principal del Punto de Venta (POS)
 *
 * Interfaz optimizada para velocidad y uso táctil con grilla de productos
 * y ticket dinámico para ventas rápidas.
 * Optimizada para dispositivos móviles con layout responsive.
 */
const POSPage: React.FC = () => {
  // Estado del ticket actual
  const [ticketItems, setTicketItems] = useState<ITicketItem[]>([]);

  // Estado para el POS seleccionado
  const [selectedPOS, setSelectedPOS] = useState<IPOS | null>(null);

  // React Query: Productos del POS con caché automático
  const {
    data: products = [],
    isLoading: isLoadingProducts,
    error: productsError,
  } = usePOSProducts(selectedPOS?.id ?? null);

  // React Query: Categorías con caché automático
  const {
    data: categoriesData,
    isLoading: isLoadingCategories,
  } = useCategories();

  const categories = categoriesData?.categories ?? [];
  const categoryNameById = categoriesData?.categoryById ?? {};

  // Estados para el modal de pago
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<
    "cliente" | "pago" | "tipoTarjeta" | "confirmacion"
  >("cliente");
  const [selectedCliente, setSelectedCliente] = useState<any>(null);
  const [_selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<string>("");
  const [searchRut, setSearchRut] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Estados para el modal de QR scanner
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [qrValidationResult, setQrValidationResult] =
    useState<QrValidationResult | null>(null);
  const [isQrProcessing, setIsQrProcessing] = useState(false);

  // Estado para el modal de entrega QR
  const [isQRDeliveryModalOpen, setIsQRDeliveryModalOpen] = useState(false);

  // Estado para el modal de confirmación de pedido
  const [isConfirmOrderModalOpen, setIsConfirmOrderModalOpen] = useState(false);

  // Estado para controlar si la lista de pedidos está colapsada
  const [isOrderListCollapsed, setIsOrderListCollapsed] = useState(false);

  const { toast } = useToast();

  // Estado de filtro de categoría
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);

  // Resetear categoría seleccionada al cambiar de POS
  useEffect(() => {
    setSelectedCategoryId(null);
  }, [selectedPOS]);

  // Mostrar error de productos si existe
  useEffect(() => {
    if (productsError) {
      toast({
        title: "Error",
        description: `No se pudieron cargar los productos${selectedPOS ? ` para ${selectedPOS.nombre}` : ''}`,
        variant: "destructive",
      });
    }
  }, [productsError, selectedPOS, toast]);

  /**
   * Productos visibles aplicando filtro de categoría (si corresponde)
   */
  const visibleProducts = useMemo(() => {
    if (selectedCategoryId == null) return products;
    return products.filter(p => p.category_id === selectedCategoryId);
  }, [products, selectedCategoryId]);

  /**
   * Categorías disponibles en el POS actual
   */
  const categoriesForPOS = useMemo(() => {
    const ids = Array.from(new Set(products.map((p) => p.category_id)));
    return ids.map((id) => ({ id, nombre: categoryNameById[id] ?? String(id) }));
  }, [products, categoryNameById]);

  /**
   * Agrega un producto al ticket
   */
  const addToTicket = (product: IPOSProduct) => {
    setTicketItems((prevItems) => {
      const existingItem = prevItems.find(
        (item) => item.productId === product.id
      );

      if (existingItem) {
        // Incrementar cantidad si ya existe
        return prevItems.map((item) =>
          item.productId === product.id
            ? {
                ...item,
                quantity: item.quantity + 1,
                subtotal: (item.quantity + 1) * item.price,
              }
            : item
        );
      } else {
        // Agregar nuevo item
        return [
          ...prevItems,
          {
            productId: product.id,
            name: product.nombre,
            price: parseFloat(product.precio),
            quantity: 1,
            subtotal: parseFloat(product.precio),
          },
        ];
      }
    });
  };

  /**
   * Incrementa la cantidad de un item
   */
  const incrementQuantity = (productId: string) => {
    setTicketItems((prevItems) =>
      prevItems.map((item) =>
        item.productId === productId
          ? {
              ...item,
              quantity: item.quantity + 1,
              subtotal: (item.quantity + 1) * item.price,
            }
          : item
      )
    );
  };

  /**
   * Decrementa la cantidad de un item
   */
  const decrementQuantity = (productId: string) => {
    setTicketItems(
      (prevItems) =>
        prevItems
          .map((item) =>
            item.productId === productId
              ? {
                  ...item,
                  quantity: Math.max(0, item.quantity - 1),
                  subtotal: Math.max(0, item.quantity - 1) * item.price,
                }
              : item
          )
          .filter((item) => item.quantity > 0) // Remover items con cantidad 0
    );
  };

  /**
   * Remueve un item del ticket
   */
  const removeFromTicket = (productId: string) => {
    setTicketItems((prevItems) =>
      prevItems.filter((item) => item.productId !== productId)
    );
  };

  /**
   * Calcula el total del ticket
   */
  const calculateTotal = (): number => {
    return ticketItems.reduce((total, item) => total + item.subtotal, 0);
  };

  /**
   * Maneja el proceso de cobro
   */
  // const handleCheckout = () => {
  //   if (ticketItems.length === 0) return;

  //   console.log("Iniciando proceso de cobro:", {
  //     items: ticketItems,
  //     total: calculateTotal(),
  //   });

  //   // TODO: Implementar lógica de cobro
  //   alert(`Total a cobrar: ${formatPrice(calculateTotal())}`);
  // };

  const handleCobrar = () => {
    if (ticketItems.length === 0) return;
    
    // Verificar el modo de operación basado en el POS seleccionado
    if (isBatchMode(selectedPOS?.id)) {
      // Modo por lotes (POS 2 - Pérgola): Mostrar modal de confirmación
      setIsConfirmOrderModalOpen(true);
    } else {
      // Modo tradicional (POS 1): Abrir modal de pago
      setIsPaymentModalOpen(true);
      setCurrentStep("cliente");
      setSelectedCliente(null);
      setSelectedPaymentMethod("");
      setSearchRut("");
      setSearchResult(null);
    }
  };

  /**
   * Maneja la confirmación de pedido para modo por lotes (POS 2)
   */
  const handleConfirmarPedido = async () => {
    if (!selectedPOS || ticketItems.length === 0) return;

    try {
      setIsProcessing(true);

      // Preparar datos del pedido para el modo por lotes
      const orderData: OrderData = {
        items: ticketItems.map(item => ({
          id: item.productId,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          categoria: "Sin categoría", // Podrías mapear esto desde los productos
          imagen: undefined
        })),
        total: calculateTotal(),
        user: {
          nombres: "Pérgola",
          apellidos: `POS ${selectedPOS.id}`,
          email: `pos${selectedPOS.id}@sistema.local`
        }
      };

      // 1. Crear el pedido
      const orderResponse = await orderService.createOrder(orderData);

      // 2. Para Pérgola (POS 2), procesar cada item como venta individual
      if (selectedPOS.id === 2) {
        console.log("Procesando ventas individuales para Pérgola...");
        
        // Iterar por cada item del ticket y crear venta + pago individual
        for (const item of ticketItems) {
          try {
            // Crear venta individual para este item
            const saleData: ISaleData = {
              items: [{
                productId: item.productId,
                quantity: item.quantity,
                price: item.price,
              }],
              total: item.subtotal, // Solo el total de este item
              posId: selectedPOS.id,
              cliente: {
                nombre: "Pérgola",
                tipo: "general"
              },
              metodoPago: "efectivo",
              fecha: new Date().toISOString(),
              paymentMethod: "cash",
            };

            // Crear la venta individual
            const sale = await POSService.processSale(saleData);
            console.log(`Venta creada para ${item.name}:`, sale);
            
            // Procesar el pago en efectivo para esta venta
            const paymentData: IPaymentData = {
              amount: item.subtotal,
              paymentMethod: "cash",
              saleId: sale.id,
              cajeroId: 'pos2-auto' // ID ficticio para el POS 2
            };

            await POSService.payBatchSaleAutomatically(sale.pedido.id, paymentData);
            console.log(`Pago procesado para ${item.name}`);
            
          } catch (itemError: any) {
            console.error(`Error procesando item ${item.name}:`, itemError);
            // Continuar con los demás items aunque uno falle
          }
        }
      } else {
        // Para otros POS, mantener la lógica original (venta única)
        const saleData: ISaleData = {
          items: ticketItems.map((item) => ({
            productId: item.productId,
            quantity: item.quantity,
            price: item.price,
          })),
          total: calculateTotal(),
          posId: selectedPOS.id,
          cliente: {
            nombre: "Pérgola",
            tipo: "general"
          },
          metodoPago: "efectivo",
          fecha: new Date().toISOString(),
          paymentMethod: "cash",
        };

        // Crear la venta
        const sale = await POSService.processSale(saleData);
        
        // Procesar el pago automáticamente en efectivo
        const paymentData: IPaymentData = {
          amount: calculateTotal(),
          paymentMethod: "cash",
          saleId: sale.id,
          cajeroId: 'pos2-auto'
        };

        await POSService.payBatchSaleAutomatically(sale.pedido.id, paymentData);
      }

      // Limpiar el ticket
      setTicketItems([]);
      setIsConfirmOrderModalOpen(false);

      toast({
        title: "Pedido Procesado",
        description: `Pedido #${orderResponse.orderId} creado y pagado exitosamente`,
      });

    } catch (error: any) {
      console.error("Error enviando pedido:", error);
      toast({
        title: "Error",
        description: error.message || "Error al procesar el pedido",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setIsConfirmOrderModalOpen(false);
    }
  };

  const handleQrScan = async (qrCodePayload: string) => {
    setIsQrProcessing(true);
    setQrValidationResult(null);

    try {
      const result = await qrService.validateAndQueueOrder(qrCodePayload);
      setQrValidationResult(result);

      if (result.success) {
        toast({
          title: "QR Válido",
          description: result.message,
        });

        // Cerrar modal después de 3 segundos
        setTimeout(() => {
          setIsQrModalOpen(false);
          setQrValidationResult(null);
        }, 3000);
      } else {
        toast({
          title: "Error de Validación",
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error procesando QR:", error);
      setQrValidationResult({
        success: false,
        message: "Error al procesar el código QR",
        timestamp: new Date().toISOString(),
      });
    } finally {
      setIsQrProcessing(false);
    }
  };

  const handleSearchCliente = async () => {
    if (!searchRut.trim()) return;
    setIsSearching(true);
    try {
      const user = await userService.findUserByRut(searchRut.trim());
      setSearchResult(user);
    } catch (err) {
      setSearchResult(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectCliente = (cliente: any) => {
    setSelectedCliente(cliente);
    setCurrentStep("pago");
  };

  const handleSelectPaymentMethod = (metodoPago: string) => {
    setSelectedPaymentMethod(metodoPago);
    if (metodoPago === "tarjeta") {
      setCurrentStep("tipoTarjeta");
    } else {
      // Ir directamente a confirmación para métodos que no requieren tipo
      setCurrentStep("confirmacion");
    }
  };

  const handleSelectCardType = (tipoTarjeta: string) => {
    setSelectedPaymentMethod(tipoTarjeta);
    setCurrentStep("confirmacion");
  };

  const handleConfirmPayment = (metodoPago: string) => {
    handleProcessPayment(metodoPago);
  };

  const handleCancelPayment = () => {
    // Volver al paso anterior según el contexto
    if (_selectedPaymentMethod === "tarjeta_debito" || _selectedPaymentMethod === "tarjeta_credito") {
      setCurrentStep("tipoTarjeta");
    } else {
      setCurrentStep("pago");
    }
  };

  const handleProcessPayment = async (metodoPago: string) => {
    if (!selectedCliente) return;
    
    // Verificar que haya un POS seleccionado
    if (!selectedPOS) {
      toast({
        title: "POS requerido",
        description: "Debe seleccionar un punto de venta antes de procesar transacciones",
        variant: "destructive",
      });
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // Verificar sesión de caja activa
      const sessionStatus = cashSessionService.getCurrentSession();
      if (!sessionStatus.isOpen || !sessionStatus.sessionId) {
        toast({
          title: "Sesión de caja requerida",
          description: "Debe iniciar una sesión de caja antes de procesar ventas",
          variant: "destructive",
        });
        setIsProcessing(false);
        return;
      }

      // Mapear el método de pago
      const paymentMethodMap: Record<string, "cash" | "card"> = {
        efectivo: "cash",
        tarjeta_debito: "card",
        tarjeta_credito: "card",
        credito_interno: "cash",
        subsidio: "cash",
      };

      const saleData: ISaleData = {
        items: ticketItems.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          price: item.price,
        })),
        total: calculateTotal(),
        posId: selectedPOS.id, // Incluir el ID del POS seleccionado
        cliente: selectedCliente,
        metodoPago: metodoPago as
          | "efectivo"
          | "tarjeta_debito"
          | "tarjeta_credito"
          | "credito_interno"
          | "subsidio",
        fecha: new Date().toISOString(),
        paymentMethod: paymentMethodMap[metodoPago] || "cash",
      };

      // Primero crear la venta
      const sale = await POSService.processSale(saleData);
      
      // Luego procesar el pago según el método
      const paymentData: IPaymentData = {
        amount: calculateTotal(),
        paymentMethod: paymentMethodMap[metodoPago] || "cash",
        saleId: sale.id,
        cajeroId: sessionStatus.cajeroId || 'unknown'
      };

      if (paymentMethodMap[metodoPago] === "cash") {
        await POSService.paySaleInCash(sale.pedido.id, paymentData);
      } else {
        await POSService.paySaleWithCard(sale.pedido.id, paymentData);
      }
      
      // Limpiar el formulario
      setTicketItems([]);
      setIsPaymentModalOpen(false);
      setCurrentStep("cliente");
      setSelectedCliente(null);
      setSelectedPaymentMethod("");
      setSearchRut("");
      setSearchResult(null);
      
      // Mensaje específico según el método de pago
      const getPaymentMessage = (metodo: string) => {
        switch (metodo) {
          case "efectivo":
            return "Venta procesada en efectivo exitosamente.";
          case "tarjeta_debito":
            return "Venta procesada con tarjeta de débito exitosamente.";
          case "tarjeta_credito":
            return "Venta procesada con tarjeta de crédito exitosamente.";
          case "credito_interno":
            return "Venta procesada con crédito interno exitosamente.";
          case "subsidio":
            return "Venta procesada con subsidio exitosamente.";
          default:
            return "La venta fue procesada exitosamente.";
        }
      };

      toast({
        title: "Venta procesada",
        description: getPaymentMessage(metodoPago),
      });
    } catch (err: any) {
      console.error("Error procesando venta:", err);
      toast({
        title: "Error",
        description: err.message || "Error al procesar la venta.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    console.log("products", products);
  }, [products]);

  // Componentes de pasos del modal
  const ClienteSelectionStep = ({
    onSelectCliente,
    searchRut,
    setSearchRut,
    onSearch,
    isSearching,
    searchResult,
    showBackButton = false,
  }: any) => (
    <div className="space-y-4">
      <Button
        variant="outline"
        className="w-full h-16 text-lg"
        onClick={() => onSelectCliente({ tipo: "general" })}
      >
        <User className="h-6 w-6 mr-2" />
        Público General
      </Button>
      <div className="space-y-2">
        <Label className="text-foreground">Buscar Cliente por RUT</Label>
        <div className="flex space-x-2">
          <Input
            value={searchRut}
            onChange={(e) => setSearchRut(e.target.value)}
            placeholder="12.345.678-9"
            disabled={isSearching}
            className="flex-1"
          />
          <Button
            onClick={onSearch}
            disabled={isSearching || !searchRut.trim()}
            className="h-10"
          >
            {isSearching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
      {searchResult && (
        <Card className="p-4 border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-foreground">
                {searchResult.nombres} {searchResult.apellidos}
              </p>
              <p className="text-sm text-muted-foreground">
                {searchResult.roles.join(", ")}
              </p>
            </div>
            <Button
              onClick={() =>
                onSelectCliente({
                  id: searchResult.id,
                  nombre: `${searchResult.nombres} ${searchResult.apellidos}`,
                  rut: searchResult.rut,
                  rol: searchResult.roles[0],
                  tipo: "registrado",
                })
              }
              className="h-10"
            >
              Seleccionar
            </Button>
          </div>
        </Card>
      )}
      {showBackButton && (
        <Button
          variant="ghost"
          onClick={() => setCurrentStep("pago")}
          className="w-full"
        >
          ← Volver
        </Button>
      )}
    </div>
  );

  const PaymentMethodStep = ({
    cliente,
    onSelectPaymentMethod,
    isProcessing,
  }: any) => {
    const getPaymentMethods = () => {
      const baseMethods = [
        { id: "efectivo", label: "Efectivo", icon: DollarSign },
        { id: "tarjeta", label: "Tarjeta", icon: CreditCard },
      ];
      if (cliente.tipo === "general") return baseMethods;
      if (cliente.rol === "Funcionario") {
        return [
          ...baseMethods,
          {
            id: "credito_interno",
            label: "Crédito Interno",
            icon: Wallet,
            variant: "default",
          },
        ];
      }
      if (cliente.rol === "Becado") {
        return [
          ...baseMethods,
          {
            id: "subsidio",
            label: "Usar Subsidio",
            icon: Gift,
            variant: "default",
          },
        ];
      }
      return baseMethods;
    };

    // Mostrar loader si está procesando
    if (isProcessing) {
      return (
        <div className="space-y-4">
          <div className="p-3 bg-muted/50 rounded-lg border border-border">
            <p className="text-sm text-foreground">
              <strong>Cliente:</strong> {cliente.nombre || "Público General"}
            </p>
          </div>
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <div className="text-center">
              <p className="font-medium text-foreground">Procesando pago...</p>
              <p className="text-sm text-muted-foreground">
                Por favor, espere mientras se completa la transacción
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="p-3 bg-muted/50 rounded-lg border border-border">
          <p className="text-sm text-foreground">
            <strong>Cliente:</strong> {cliente.nombre || "Público General"}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {getPaymentMethods().map((method) => (
            <Button
              key={method.id}
              variant={
                ("variant" in method && (method.variant as any)) || "outline"
              }
              className="h-16 flex flex-col space-y-1"
              onClick={() => onSelectPaymentMethod(method.id)}
              disabled={isProcessing}
            >
              <method.icon className="h-5 w-5" />
              <span className="text-sm">{method.label}</span>
            </Button>
          ))}
        </div>
        <Button
          variant="ghost"
          onClick={() => setCurrentStep("cliente")}
          className="w-full"
        >
          ← Volver
        </Button>
      </div>
    );
  };

  const CardTypeStep = ({ onSelectCardType, isProcessing }: any) => {
    // Mostrar loader si está procesando
    if (isProcessing) {
      return (
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Método seleccionado:</strong> Tarjeta
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
              Seleccione el tipo de tarjeta
            </p>
          </div>
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <div className="text-center">
              <p className="font-medium text-foreground">
                Procesando pago con tarjeta...
              </p>
              <p className="text-sm text-muted-foreground">
                Por favor, espere mientras se completa la transacción
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <strong>Método seleccionado:</strong> Tarjeta
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
            Seleccione el tipo de tarjeta
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            className="h-16 flex flex-col space-y-1"
            onClick={() => onSelectCardType("tarjeta_debito")}
            disabled={isProcessing}
          >
            <CreditCard className="h-5 w-5" />
            <span className="text-sm">Débito</span>
          </Button>
          <Button
            variant="outline"
            className="h-16 flex flex-col space-y-1"
            onClick={() => onSelectCardType("tarjeta_credito")}
            disabled={isProcessing}
          >
            <CreditCard className="h-5 w-5" />
            <span className="text-sm">Crédito</span>
          </Button>
        </div>
        <Button
          variant="ghost"
          onClick={() => setCurrentStep("pago")}
          className="w-full"
        >
          ← Volver
        </Button>
      </div>
    );
  };

  const ConfirmationStep = ({ 
    cliente, 
    metodoPago, 
    total, 
    onConfirm, 
    onCancel, 
    isProcessing 
  }: any) => {
    const getPaymentMethodLabel = (metodo: string) => {
      const labels: Record<string, string> = {
        efectivo: "Efectivo",
        tarjeta_debito: "Tarjeta de Débito", 
        tarjeta_credito: "Tarjeta de Crédito",
        credito_interno: "Crédito Interno",
        subsidio: "Subsidio"
      };
      return labels[metodo] || metodo;
    };

    const getPaymentIcon = (metodo: string) => {
      switch (metodo) {
        case "efectivo":
          return DollarSign;
        case "tarjeta_debito":
        case "tarjeta_credito":
          return CreditCard;
        case "credito_interno":
          return Wallet;
        case "subsidio":
          return Gift;
        default:
          return DollarSign;
      }
    };

    const PaymentIcon = getPaymentIcon(metodoPago);

    // Mostrar loader si está procesando
    if (isProcessing) {
      return (
        <div className="space-y-4">
          <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-center gap-3 mb-3">
              <PaymentIcon className="h-6 w-6 text-primary" />
              <div>
                <p className="font-medium text-foreground">
                  {getPaymentMethodLabel(metodoPago)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Cliente: {cliente.nombre || "Público General"}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-primary">
                {formatPrice(total)}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <div className="text-center">
              <p className="font-medium text-foreground">Procesando pago...</p>
              <p className="text-sm text-muted-foreground">
                Por favor, espere mientras se completa la transacción
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Resumen de la venta */}
        <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center gap-3 mb-3">
            <PaymentIcon className="h-6 w-6 text-primary" />
            <div>
              <p className="font-medium text-foreground">
                {getPaymentMethodLabel(metodoPago)}
              </p>
              <p className="text-sm text-muted-foreground">
                Cliente: {cliente.nombre || "Público General"}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-primary">
              {formatPrice(total)}
            </p>
          </div>
        </div>

        {/* Instrucciones según el método de pago */}
        <div className="p-4 bg-muted/50 rounded-lg border">
          <p className="text-sm font-medium text-foreground mb-2">
            Instrucciones para el cajero:
          </p>
          {metodoPago === "efectivo" && (
            <div className="text-sm text-muted-foreground space-y-1">
              <p>• Reciba el dinero en efectivo del cliente</p>
              <p>• Verifique que el monto sea correcto</p>
              <p>• Entregue el cambio si es necesario</p>
              <p>• Confirme cuando haya completado la transacción</p>
            </div>
          )}
          {(metodoPago === "tarjeta_debito" || metodoPago === "tarjeta_credito") && (
            <div className="text-sm text-muted-foreground space-y-1">
              <p>• Inserte o acerque la tarjeta al terminal</p>
              <p>• Espere la autorización del banco</p>
              <p>• Solicite PIN si es requerido</p>
              <p>• Confirme cuando la transacción sea aprobada</p>
            </div>
          )}
          {metodoPago === "credito_interno" && (
            <div className="text-sm text-muted-foreground space-y-1">
              <p>• Se descontará del crédito interno del usuario</p>
              <p>• Verifique que el usuario tenga saldo suficiente</p>
              <p>• Confirme para procesar el descuento</p>
            </div>
          )}
          {metodoPago === "subsidio" && (
            <div className="text-sm text-muted-foreground space-y-1">
              <p>• Se utilizará el subsidio disponible del estudiante</p>
              <p>• Verifique que tenga subsidio suficiente</p>
              <p>• Confirme para procesar el pago</p>
            </div>
          )}
        </div>

        {/* Botones de acción */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onCancel}
            className="flex-1"
            disabled={isProcessing}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => onConfirm(metodoPago)}
            className="flex-1"
            disabled={isProcessing}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Confirmar Pago
          </Button>
        </div>
      </div>
    );
  };

  // categorías ya se cargan vía efecto; mantenemos esta sección para coherencia visual

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-48px)] bg-background relative">
        {/* Columna izquierda - Productos (70% en desktop cuando panel derecho está abierto, 100% cuando está colapsado) */}
        <div className={`w-full p-4 lg:p-6 overflow-y-auto bg-background transition-all duration-300 ${
          isOrderListCollapsed 
            ? 'lg:w-full' 
            : 'lg:w-[70%]'
        }`}>
        {/* Selección de POS */}
        <POSSelection className="mb-4" onPOSChange={setSelectedPOS} />
        
        {/* Estado de la sesión de caja - Solo para modo tradicional */}
        {!isBatchMode(selectedPOS?.id) && (
          <CashSessionStatus className="mb-4" posId={selectedPOS?.id} />
        )}
        
        <div className="mb-6">
          <h1 className={`font-bold text-foreground mb-2 ${
            isBatchMode(selectedPOS?.id) 
              ? 'text-3xl lg:text-4xl' 
              : 'text-2xl'
          }`}>
            {isBatchMode(selectedPOS?.id) ? "Crear Pedido Rápido" : "Productos Disponibles"}
          </h1>
          <p className={`text-muted-foreground ${
            isBatchMode(selectedPOS?.id) ? 'text-lg font-medium' : ''
          }`}>
            {selectedPOS 
              ? isBatchMode(selectedPOS?.id)
                ? `${selectedPOS.nombre} - Modo rápido: Agregue productos y confirme cuando esté listo`
                : `Productos de ${selectedPOS.nombre} - Haz clic en un producto para agregarlo al ticket`
              : "Seleccione un punto de venta para ver los productos disponibles"
            }
          </p>
          <p className="text-muted-foreground">Filtre por categorías</p>
          {/* Botones de categorías disponibles para el POS seleccionado */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedCategoryId == null ? "default" : "outline"}
              onClick={() => setSelectedCategoryId(null)}
            >
              Todas
            </Button>
            {isLoadingCategories && categoriesForPOS.length === 0
              ? Array.from({ length: 4 }).map((_, idx) => (
                  <div key={idx} className="h-9 w-24 bg-muted rounded" />
                ))
              : categoriesForPOS.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategoryId === category.id ? "default" : "outline"}
                    onClick={() => setSelectedCategoryId(category.id)}
                  >
                    {category.nombre}
                  </Button>
                ))}
          </div>
        </div>

        {/* Mensaje cuando no hay POS seleccionado */}
        {!selectedPOS ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Store className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-muted-foreground mb-2">
              Punto de Venta Requerido
            </h3>
            <p className="text-muted-foreground max-w-md">
              Seleccione un punto de venta en la parte superior para ver los productos disponibles
              y comenzar a procesar ventas.
            </p>
          </div>
        ) : (
          /* Grilla de productos responsive */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 lg:gap-4">
            {isLoadingProducts
              ? // Estado de carga
                Array.from({ length: 10 }).map((_, index) => (
                  <Card key={index} className="animate-pulse">
                    <CardContent className="p-3 lg:p-4">
                      <div className="w-full h-20 lg:h-24 bg-muted rounded-lg mb-3"></div>
                      <div className="space-y-2">
                        <div className="h-4 bg-muted rounded"></div>
                        <div className="flex items-center justify-between">
                          <div className="h-3 w-16 bg-muted rounded"></div>
                          <div className="h-4 w-20 bg-muted rounded"></div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              : visibleProducts.length === 0 ? (
                // No hay productos para este POS
                <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                  <Package className="h-12 w-12 text-muted-foreground mb-3" />
                  <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                    Sin Productos Disponibles
                  </h3>
                  <p className="text-muted-foreground">
                    No hay productos configurados para {selectedPOS.nombre}
                  </p>
                </div>
              ) : (
                // Productos disponibles (filtrados)
                visibleProducts.map((product) => (
                <Card
                  key={product.id}
                  className={`cursor-pointer hover:shadow-lg transition-all duration-200 ${
                    isBatchMode(selectedPOS?.id)
                      ? 'border-2 border-green-200 hover:border-green-400 hover:shadow-green-100 bg-gradient-to-br from-white to-green-50 dark:from-gray-900 dark:to-green-950/20 hover:scale-105'
                      : 'border-2 hover:border-primary'
                  } ${
                    !product.esta_disponible
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  onClick={() =>
                    product.esta_disponible && addToTicket(product)
                  }
                >
                  <CardContent className="p-3 lg:p-4">
                    {/* Imagen del producto */}
                    <div className={`w-full mb-3 rounded-lg flex items-center justify-center ${
                      isBatchMode(selectedPOS?.id) 
                        ? 'h-24 lg:h-28 bg-muted' 
                        : 'h-20 lg:h-24 bg-muted'
                    }`}>
                      {product.imagen_url ? (
                        <img
                          src={product.imagen_url}
                          alt={product.nombre}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <ShoppingCart className="h-6 w-6 lg:h-8 lg:w-8 text-muted-foreground" />
                      )}
                    </div>

                    {/* Información del producto */}
                    <div className="space-y-2">
                      <h3 className={`font-semibold text-foreground line-clamp-2 ${
                        isBatchMode(selectedPOS?.id) 
                          ? 'text-sm lg:text-base' 
                          : 'text-xs lg:text-sm'
                      }`}>
                        {product.nombre}
                      </h3>

                      <div className="flex items-center justify-between">
                        {/* category name */}
                        <span className="text-muted-foreground text-xs">
                          {categories.find((category) => category.id === product.category_id)?.nombre}
                        </span>
                        <span className={`font-bold ${
                          isBatchMode(selectedPOS?.id)
                            ? 'text-primary text-base lg:text-xl'
                            : 'text-primary text-sm lg:text-lg'
                        }`}>
                          {formatPrice(parseFloat(product.precio))}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                ))
              )}
          </div>
        )}
        </div>

        {/* Columna derecha - Ticket (30% en desktop, 100% en mobile) */}
        {!isOrderListCollapsed && (
          <div className="w-full lg:w-[30%] bg-card border-t lg:border-l lg:border-t-0 flex flex-col transition-all duration-300">
            {/* Header del ticket */}
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-foreground mb-2">
                    {isBatchMode(selectedPOS?.id) ? "Pedidos del dia" : "Ticket de Venta"}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {ticketItems.length}{" "}
                    {ticketItems.length === 1 ? "producto" : "productos"}
                  </p>
                </div>
                
                {/* Botón para colapsar el panel */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOrderListCollapsed(true)}
                  className="h-8 w-8 p-0"
                  title="Ocultar panel"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Lista de items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {ticketItems.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {isBatchMode(selectedPOS?.id) 
                      ? "No hay productos en el pedido" 
                      : "No hay productos en el ticket"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Haz clic en los productos para agregarlos
                  </p>
                </div>
              ) : (
                ticketItems.map((item) => (
                  <div
                    key={item.productId}
                    className="flex items-center justify-between p-3 bg-background rounded-lg border border-border"
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm text-foreground truncate">
                        {item.name}
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        {formatPrice(item.price)} c/u
                      </p>
                    </div>

                    <div className="flex items-center space-x-2 ml-3">
                      {/* Controles de cantidad */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          decrementQuantity(item.productId);
                        }}
                        className="h-8 w-8 p-0"
                      >
                        <Minus className="h-3 w-3" />
                      </Button>

                      <span className="font-bold text-sm min-w-[2rem] text-center text-foreground">
                        {item.quantity}
                      </span>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          incrementQuantity(item.productId);
                        }}
                        className="h-8 w-8 p-0"
                      >
                        <Plus className="h-3 w-3" />
                      </Button>

                      {/* Botón eliminar */}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFromTicket(item.productId);
                        }}
                        className="h-8 w-8 p-0"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>

                    <div className="ml-3 text-right">
                      <p className="font-bold text-sm text-foreground">
                        {formatPrice(item.subtotal)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer del ticket con total y botón principal */}
            <div className="p-4 border-t border-border bg-muted/50">
              <div className="mb-4">
                <div className="flex justify-between items-center text-lg font-bold">
                  <span className="text-foreground">Total:</span>
                  <span className="text-2xl text-primary">
                    {formatPrice(calculateTotal())}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Button
                  onClick={handleCobrar}
                  disabled={ticketItems.length === 0 || isProcessing}
                  className="w-full h-12 text-lg font-bold"
                  size="lg"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {isBatchMode(selectedPOS?.id) ? "Enviando..." : "Procesando..."}
                    </>
                  ) : (
                    isBatchMode(selectedPOS?.id) ? "Confirmar Pedidos del dia" : "Cobrar"
                  )}
                </Button>

                {/* Sistema de Entrega QR - Solo para modo tradicional */}
                {/* {!isBatchMode(selectedPOS?.id) && (
                  <Button
                    onClick={() => setIsQRDeliveryModalOpen(true)}
                    variant="outline"
                    className="w-full h-12"
                    size="lg"
                  >
                    <QrCode className="h-4 w-4 mr-2" />
                    Sistema de Entrega QR
                  </Button>
                )} */}
              </div>
            </div>
          </div>
        )}

        {/* Botón flotante para expandir el panel cuando está colapsado */}
        {isOrderListCollapsed && (
          <Button
            variant="default"
            size="sm"
            onClick={() => setIsOrderListCollapsed(false)}
            className="fixed top-1/2 right-4 transform -translate-y-1/2 z-50 h-8 w-8 rounded-full shadow-lg hidden lg:flex"
            title="Mostrar pedido en curso"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}

        {/* Modal de Pago */}
        <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {currentStep === "cliente" && "Finalizar Venta"}
              {currentStep === "pago" && "Seleccionar Método de Pago"}
              {currentStep === "tipoTarjeta" && "Tipo de Tarjeta"}
              {currentStep === "confirmacion" && "Confirmar Pago"}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Total a cobrar: {formatPrice(calculateTotal())}
            </DialogDescription>
          </DialogHeader>
          {currentStep === "cliente" && (
            <ClienteSelectionStep
              onSelectCliente={handleSelectCliente}
              searchRut={searchRut}
              setSearchRut={setSearchRut}
              onSearch={handleSearchCliente}
              isSearching={isSearching}
              searchResult={searchResult}
              showBackButton={false}
            />
          )}
          {currentStep === "pago" && selectedCliente && (
            <PaymentMethodStep
              cliente={selectedCliente}
              onSelectPaymentMethod={handleSelectPaymentMethod}
              isProcessing={isProcessing}
            />
          )}
          {currentStep === "tipoTarjeta" && (
            <CardTypeStep
              onSelectCardType={handleSelectCardType}
              isProcessing={isProcessing}
            />
          )}
          {currentStep === "confirmacion" && selectedCliente && _selectedPaymentMethod && (
            <ConfirmationStep
              cliente={selectedCliente}
              metodoPago={_selectedPaymentMethod}
              total={calculateTotal()}
              onConfirm={handleConfirmPayment}
              onCancel={handleCancelPayment}
              isProcessing={isProcessing}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de QR Scanner */}
      <Dialog open={isQrModalOpen} onOpenChange={setIsQrModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <QrCode className="h-5 w-5" />
              Escanear Código QR
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Escanea el código QR del cliente para validar su pedido
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Área del scanner */}
            <div className="flex justify-center">
              <QrScanner
                onScan={handleQrScan}
                showFeedback={false}
                autoReset={false}
                className="w-full max-w-md"
              />
            </div>

            {/* Resultado de validación */}
            {qrValidationResult && (
              <div
                className={`p-4 rounded-lg border-2 ${
                  qrValidationResult.success
                    ? "bg-primary/10 border-primary/20"
                    : "bg-destructive/10 border-destructive/20"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {qrValidationResult.success ? (
                    <CheckCircle className="h-5 w-5 text-primary" />
                  ) : (
                    <XCircle className="h-5 w-5 text-destructive" />
                  )}
                  <span
                    className={`font-medium ${
                      qrValidationResult.success
                        ? "text-primary"
                        : "text-destructive"
                    }`}
                  >
                    {qrValidationResult.success
                      ? "QR Válido"
                      : "Error de Validación"}
                  </span>
                </div>
                <p
                  className={`text-sm ${
                    qrValidationResult.success
                      ? "text-primary/80"
                      : "text-destructive/80"
                  }`}
                >
                  {qrValidationResult.message}
                </p>
              </div>
            )}

            {/* Indicador de procesamiento */}
            {isQrProcessing && (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary mr-3" />
                <span className="text-sm text-muted-foreground">
                  Procesando QR...
                </span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Sistema de Entrega QR */}
      <QRDeliveryModal
        isOpen={isQRDeliveryModalOpen}
        onClose={() => setIsQRDeliveryModalOpen(false)}
      />

      {/* Modal de Confirmación de Pedido (Solo para POS 2 - Pérgola) */}
      <AlertDialog open={isConfirmOrderModalOpen} onOpenChange={setIsConfirmOrderModalOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              Confirmar ventas del dia - Pérgola
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro que desea confirmar los pedidos de este dia? Se procesará automáticamente y se registrará en el sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-3">
            <div className="bg-muted p-3 rounded-lg">
              <h4 className="font-medium text-foreground mb-2">Resumen del dia:</h4>
              <div className="space-y-1">
                {ticketItems.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {item.quantity}x {item.name}
                    </span>
                    <span className="font-medium text-foreground">
                      {formatPrice(item.subtotal)}
                    </span>
                  </div>
                ))}
                <div className="border-t border-border pt-2 mt-2">
                  <div className="flex justify-between font-bold text-foreground">
                    <span>Total:</span>
                    <span>{formatPrice(calculateTotal())}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarPedido}
              disabled={isProcessing}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Confirmar Pedido
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default POSPage;
